import type { CombinedInstrumentListItem } from "@repo/shared/types";
import { LineChart, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { InstrumentSyncDot } from "@/pages/settings/portfolios/detail/products/instrument-sync-status";
import { T } from "./combined-instruments-table.i18n";

type CombinedInstrumentsTableProps = {
  items: CombinedInstrumentListItem[];
  currentPage: number;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onEdit: (item: CombinedInstrumentListItem) => void;
  onDelete: (item: CombinedInstrumentListItem) => void;
  onNameClick: (item: CombinedInstrumentListItem) => void;
  isLoading?: boolean;
};

export function CombinedInstrumentsTable({
  items,
  currentPage,
  totalCount,
  totalPages,
  onPageChange,
  onEdit,
  onDelete,
  onNameClick,
  isLoading,
}: CombinedInstrumentsTableProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  if (isLoading && items.length === 0) {
    return (
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.isin}</TableHead>
            <TableHead>{t.currency}</TableHead>
            <TableHead className="w-20">{t.status}</TableHead>
            <TableHead className="w-20 text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {["a", "b", "c", "d", "e"].map((key) => (
            <TableRow key={key}>
              <TableCell>
                <Skeleton className="h-4 w-32" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-2.5 w-2.5 rounded-full" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-8 w-16 ml-auto" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (totalCount === 0) {
    return <EmptyState>{t.noInstruments}</EmptyState>;
  }

  return (
    <div className="space-y-4">
      <Table
        variant="data"
        containerClassName={isLoading ? "opacity-50 pointer-events-none transition-opacity" : ""}
      >
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.isin}</TableHead>
            <TableHead>{t.currency}</TableHead>
            <TableHead className="w-20">{t.status}</TableHead>
            <TableHead className="w-20 text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{item.isin}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{item.currencyIso}</TableCell>
              <TableCell>
                <InstrumentSyncDot
                  health={item.instrumentSync}
                  lang={userLanguage}
                  variant="recompute"
                  nullLabel={t.notComputed}
                />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onNameClick(item)}
                    disabled={isLoading}
                    tooltip={t.viewChart}
                  >
                    <LineChart className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(item)}
                    disabled={isLoading}
                    tooltip={t.edit}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(item)}
                    disabled={isLoading}
                    tooltip={t.delete}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
          >
            {t.previous}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
          >
            {t.next}
          </Button>
        </div>
      )}
    </div>
  );
}
